import React, { useImperativeHandle } from 'react';
import { Tag } from '../api/Tag';
import { Author } from '../api/Author';
import useFilterBarPanels, { FILTER_BAR_TRANSITION_DURATION } from '../hooks/ui/useFilterBarPanels';
import { Box, Button, Checkbox, Flex, Heading, Icon, OverlayPanel, Radio } from './ui';

interface SortField {
  key: string;
  label: string;
}

interface SortDirection {
  key: 'asc' | 'desc';
  label: string;
  icon: string;
}

interface FilterBarProps {
  allTags: Tag[];
  allAuthors: Author[];
  selectedTagIds: number[];
  selectedAuthorIds: number[];
  onTagChange: (tagId: number, isChecked: boolean) => void;
  onAuthorChange: (authorId: number, isChecked: boolean) => void;
  onClearFilters: () => void;
  isOpen: boolean; // Filter panel open state
  onToggle: () => void; // Filter panel toggle handler
  navbarHeight: number; // New prop for dynamic navbar height

  // New props for sorting
  sortFields: SortField[];
  selectedSortField: string;
  sortDirections: SortDirection[];
  selectedSortDirection: 'asc' | 'desc';
  onSortFieldChange: (field: string) => void;
  onSortDirectionChange: (direction: 'asc' | 'desc') => void;
  displayMode: string; // NEW: Current display mode (grid/list)
  onToggleDisplayMode: () => void; // NEW: Toggle display mode handler
}

export interface FilterBarHandle {
  closeAllPanels: () => void;
}

const FilterBar = React.forwardRef<FilterBarHandle, FilterBarProps>((props, ref) => {
  const {
    allTags,
    allAuthors,
    selectedTagIds,
    selectedAuthorIds,
    onTagChange,
    onAuthorChange,
    onClearFilters,
    isOpen,
    onToggle,
    sortFields,
    selectedSortField,
    sortDirections,
    selectedSortDirection,
    onSortFieldChange,
    onSortDirectionChange,
    displayMode, // NEW
    onToggleDisplayMode, // NEW
  } = props; // Destructure props inside the component body

  const hasActiveFilters = selectedTagIds.length > 0 || selectedAuthorIds.length > 0;
  const {
    isSortPanelOpen,
    shouldRenderFilterOverlay,
    shouldRenderSortOverlay,
    toggleSortPanel,
    toggleFilterPanel,
    closeAllPanels,
  } = useFilterBarPanels({
    isFilterPanelOpen: isOpen,
    onToggleFilterPanel: onToggle,
  });

  useImperativeHandle(ref, () => ({
    closeAllPanels,
  }), [closeAllPanels]);

  return (
    <>
      {/* Backdrop to prevent interaction with content behind and close panels on click */}
      {(shouldRenderFilterOverlay || shouldRenderSortOverlay) && (
        <Box
          className="filter-backdrop"
          onClick={closeAllPanels}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1030, // Below FilterBar (1031) and Panels (1032), above content
            cursor: 'default',
            // Transparent background to keep content visible but not clickable
            backgroundColor: 'transparent'
          }}
          aria-hidden="true"
        />
      )}

      <Flex
        className="filter-bar"
        background="light"
        shadow="sm"
        direction="column"
        directionAt={{ sm: 'row' }}
        justify="between"
        align="stretch"
        paddingY={2}
        paddingX={3}
        style={{ position: 'sticky', zIndex: 1031 }}
        role="toolbar"
        aria-label="Barre d'outils de filtrage et tri"
      >
        <Flex gap={2} marginBottom={2} marginBottomAt={{ sm: 0 }}>
          <Button
            variant="primary"
            outline
            active={isOpen}
            fullWidth
            onClick={toggleFilterPanel}
            aria-expanded={isOpen}
            aria-controls="filter-panel"
            aria-label={`Filtres${hasActiveFilters ? ` (${selectedTagIds.length + selectedAuthorIds.length} actifs)` : ''}`}
            style={{ minHeight: '44px' }}
          >
            <Icon name="funnel" marginEnd={2} />
            Filtres {hasActiveFilters && `(${selectedTagIds.length + selectedAuthorIds.length})`}
          </Button>

          <Button
            variant="primary"
            outline
            active={isSortPanelOpen}
            fullWidth
            onClick={toggleSortPanel}
            aria-expanded={isSortPanelOpen}
            aria-controls="sort-panel"
            aria-label={`Trier par ${sortFields.find(f => f.key === selectedSortField)?.label || ''} ${sortDirections.find(d => d.key === selectedSortDirection)?.label || ''}`}
            style={{ minHeight: '44px' }}
          >
            <Icon name="sort-alpha-down" marginEnd={2} />
            Trier ({sortFields.find(f => f.key === selectedSortField)?.label || ''} {sortDirections.find(d => d.key === selectedSortDirection)?.icon || ''})
          </Button>

          <Button
            variant="primary"
            outline
            fullWidth
            onClick={onToggleDisplayMode}
            aria-label={displayMode === 'grid' ? 'Afficher en mode liste' : 'Afficher en mode grille'}
            aria-pressed={displayMode === 'grid'}
            style={{ minHeight: '44px' }}
          >
            <Box as="span" aria-hidden="true">{displayMode === 'grid' ? 'Grille ▦' : 'Liste 📜'}</Box>
          </Button>
        </Flex>

        {hasActiveFilters && (
          <Button
            variant="secondary"
            outline
            size="sm"
            flexGrow
            onClick={onClearFilters}
            aria-label={`Effacer les ${selectedTagIds.length + selectedAuthorIds.length} filtres actifs`}
            style={{ minHeight: '44px' }}
          >
            Effacer les filtres
          </Button>
        )}

        {shouldRenderFilterOverlay && (
          <OverlayPanel
            id="filter-panel"
            className="filter-options-overlay"
            role="region"
            aria-labelledby="filter-heading"
            aria-hidden={!isOpen}
            style={{
              left: 0,
              right: 0,
              top: '100%',
              zIndex: 1032,
              maxHeight: isOpen ? '500px' : '0',
              overflow: 'hidden',
              transition: `max-height ${FILTER_BAR_TRANSITION_DURATION}ms ease-in-out`,
            }}
          >
            <Box marginBottom={3}>
              <Heading as="h6" id="filter-tags-heading" textColor="primary">Tags</Heading>
              <Flex wrap role="group" aria-labelledby="filter-tags-heading">
                {allTags.map(tag => (
                  <Checkbox
                    key={tag.id}
                    id={`tag-${tag.id}`}
                    label={tag.name}
                    inline
                    checked={selectedTagIds.includes(tag.id)}
                    onChange={(e) => onTagChange(tag.id, e.target.checked)}
                  />
                ))}
              </Flex>
            </Box>

            <Box>
              <Heading as="h6" id="filter-authors-heading" textColor="primary">Auteurs</Heading>
              <Flex wrap role="group" aria-labelledby="filter-authors-heading">
                {allAuthors.map(author => (
                  <Checkbox
                    key={author.id}
                    id={`author-${author.id}`}
                    label={`${author.first_name} ${author.name}`}
                    inline
                    checked={selectedAuthorIds.includes(author.id)}
                    onChange={(e) => onAuthorChange(author.id, e.target.checked)}
                  />
                ))}
              </Flex>
            </Box>
          </OverlayPanel>
        )}

        {shouldRenderSortOverlay && (
          <OverlayPanel
            id="sort-panel"
            className="sort-options-overlay"
            role="region"
            aria-labelledby="sort-heading"
            aria-hidden={!isSortPanelOpen}
            style={{
              left: 0,
              right: 0,
              top: '100%',
              zIndex: 1032,
              maxHeight: isSortPanelOpen ? '300px' : '0', // Adjust max-height as needed
              overflow: 'hidden',
              transition: `max-height ${FILTER_BAR_TRANSITION_DURATION}ms ease-in-out`,
            }}
          >
            <Heading as="h6" id="sort-heading" marginBottom={3} textColor="primary">Trier par</Heading>

            <Box marginBottom={3}>
              <Heading as="h6" id="sort-field-heading" textColor="secondary" style={{ fontSize: '0.9rem' }}>Champ</Heading>
              <Box role="radiogroup" aria-labelledby="sort-field-heading">
                {sortFields.map(field => (
                  <Radio
                    key={field.key}
                    id={`sort-field-${field.key}`}
                    label={field.label}
                    name="sortField"
                    value={field.key}
                    checked={selectedSortField === field.key}
                    onChange={() => onSortFieldChange(field.key)}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Heading as="h6" id="sort-direction-heading" textColor="secondary" style={{ fontSize: '0.9rem' }}>Direction</Heading>
              <Box role="radiogroup" aria-labelledby="sort-direction-heading">
                {sortDirections.map(direction => (
                  <Radio
                    key={direction.key}
                    id={`sort-direction-${direction.key}`}
                    label={`${direction.label} ${direction.icon}`}
                    name="sortDirection"
                    value={direction.key}
                    checked={selectedSortDirection === direction.key}
                    onChange={() => onSortDirectionChange(direction.key)}
                  />
                ))}
              </Box>
            </Box>
          </OverlayPanel>
        )}
      </Flex>
    </>
  );
});

export default FilterBar;
